import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import Loading from '../Component/Loading';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faHandshake, faPersonCirclePlus } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import SEOHelmet from '../Component/SEOHelmet';
import NoDataFull from './NoDataFull'; // Import the NoDataFull component

const Header = lazy(() => import('../Component/Header'));
const Footer = lazy(() => import('../Component/Footer'));
const Touch = lazy(() => import('../Component/Home/Touch'));
const JobDetails = lazy(() => import('../Component/Home/JobDetails'));

const JobDetailsPage = () => {
    const [loading, setLoading] = useState(true);
    const [jobDetails, setJobDetails] = useState();
    const [data, setData] = useState();
    const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);
    const [apiError, setApiError] = useState(false); // Add API error state
    const { id } = useParams();

    const handleClick = () => {
        window.location.href = '/hiring';
    };
    
    const getData = useCallback(() => {
        setLoading(true);
        setApiError(false); // Reset error state
        
        axios.get(`https://api.ps-link.world/api/plexus/position/read/${id}`)
            .then((res) => {
                const data = res.data.data;
                setJobDetails(data);
                setLoading(false);
                setApiError(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Failed to fetch job details.");
                setLoading(false);
                setApiError(true); // Set error state when API fails
            });
    }, [id]);

    // Check if jobDetails has valid data
    const hasJobDetailsData = () => {
        if (!jobDetails) return false;
        
        if (Array.isArray(jobDetails)) {
            // If it's an array, check if it has items and they're not disabled
            const activeItems = jobDetails.filter(item => item.status !== false);
            return activeItems.length > 0;
        }
        
        if (typeof jobDetails === 'object') {
            // Check if object has keys
            const keys = Object.keys(jobDetails);
            if (keys.length === 0) return false;
            
            // Check if the job is not disabled/inactive
            if (jobDetails.status === false) return false;
            
            // If jobDetails has essential properties, consider it valid
            if (jobDetails.position || jobDetails.title || jobDetails._id) {
                return true;
            }
            
            // Check if object has meaningful content
            return keys.length > 0;
        }
        
        // Check if jobDetails is exactly 0, null, or undefined
        if (jobDetails === 0 || jobDetails === null || jobDetails === undefined) return false;
        
        return true;
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        getData();

        const storedData = sessionStorage.getItem('hiringStatus');
        if (storedData) {
            setData(JSON.parse(storedData)); // Parse the JSON string
        }

        const handleScroll = () => {
            if (window.scrollY > 200) {
                setIsScrollTopVisible(true);
            } else {
                setIsScrollTopVisible(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [getData]);

    // Show NoDataFull only after loading is complete and either API failed or no data exists
    if (!loading && (apiError || !hasJobDetailsData())) {
        return <NoDataFull />;
    }

    return (
        <div>
            <SEOHelmet
                title="Job Details | Plexus Technology"
                description="We're looking for passionate and talented individuals to shape the future of technology. Apply now and take the next step in your career with Plexus Technology."
                keywords="Job qualifications , Required skills , Key skills needed , Essential qualifications , Desired experience , Job prerequisites , Required education , Necessary expertise , Job requirements , Professional skills , Employee benefits , Job benefits , Plexus employment opportunities , Benefits of working at Plexus , Plexus job description , Start your career at Plexus , Plexus job openings , IT jobs at Plexus , IT career at Plexus , Plexus job requirements , Benefits of working at Plexus , Apply to Plexus Technology , Plexus job description"
                ogTitle="Job Details | Plexus Technology"
                ogDescription="We're looking for passionate and talented individuals to shape the future of technology. Apply now and take the next step in your career with Plexus Technology."
                ogImage="https://plexus-technology.in/static/media/logo.589291ad7a3760919228.png"
                twitterCard="summary_large_image"
            />

            {loading ? (
                <Loading />
            ) : (
                <Suspense fallback={<Loading />} >
                    <Header />
                    <JobDetails data={jobDetails}/>
                    <Touch />
                    <Footer />
                </Suspense>
            )}
            <>
                <div className="fixed-button d-flex">
                    <div className="w-auto mx-auto d-flex gap-3 flex-wrap align-items-center justify-content-center">
                        {data === "true" && (
                            <button
                                className="button1"
                                onClick={handleClick}
                            >
                                <FontAwesomeIcon icon={faPersonCirclePlus} className="me-2" />
                                We're Hiring
                            </button>
                        )}

                        <a
                            href="/portfolio"
                            className="button2 text-decoration-none"
                        >
                            <FontAwesomeIcon icon={faHandshake} className="me-2" />
                            Let’s Collaborate
                        </a>
                    </div>
                </div>
            </>

            {isScrollTopVisible && (
                <p className='fixed-top-btn cursor' onClick={scrollToTop}>
                    <FontAwesomeIcon icon={faArrowUp} className='px-2' />
                </p>
            )}
        </div>
    );
};

export default JobDetailsPage;